import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import distributorService from '../../services/distributorService';
import {
  fundingConfiguration,
  readFundingSettings,
  sameFundingConfiguration,
  validFundingConfiguration,
} from './cashFundingSettingsContract';

const unwrap = (response) => response?.data?.data;
export default function CashFundingSettings({ distributor, disabled, onLockChange }) {
  const t = useT();
  const [saved, setSaved] = useState(null),
    [draft, setDraft] = useState(null);
  const [available, setAvailable] = useState(true),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [uncertain, setUncertain] = useState(false),
    [expanded, setExpanded] = useState(false),
    [retry, setRetry] = useState(0);
  const pending = useRef(false),
    callback = useRef(onLockChange);
  const session = useRef(captureSession()),
    scope = useRef(shippingAccessScope());
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  callback.current = onLockChange;
  const { begin, version } = useShippingRequest(`cash-funding-settings:${distributor._id}`);
  const configurationFor = (row) => fundingConfiguration(row.policy, row.schemaVersion);
  const dirty = Boolean(
    saved && draft && !sameFundingConfiguration(draft, configurationFor(saved))
  );
  useEffect(() => {
    callback.current?.(busy || dirty || uncertain);
    return () => callback.current?.(false);
  }, [busy, dirty, uncertain]);
  useEffect(() => {
    const request = begin();
    setSaved(null);
    setDraft(null);
    setError('');
    setNotice('');
    setUncertain(false);
    setAvailable(true);
    const loading = distributorService.adminCashFundingSettings(distributor._id, {
      signal: request.signal,
    });
    if (!loading) {
      setAvailable(false);
      return;
    }
    Promise.resolve(loading)
      .then((response) => {
        if (!request.isCurrent()) return;
        const row = readFundingSettings(unwrap(response));
        setSaved(row);
        setDraft(configurationFor(row));
      })
      .catch((failure) => {
        if (!request.isCurrent()) return;
        if (failure?.response?.status === 404) setAvailable(false);
        else setError('Cash payment setup could not load. Retry before editing.');
      });
  }, [begin, version, distributor._id, retry]);
  const edit = (next) => {
    if (!current() || disabled || busy || uncertain || distributor.isActive !== true) return;
    const mappingChanged = !sameFundingConfiguration(
      {
        ...next,
        approvalReference: draft.approvalReference,
        confirmedNoDuplicateProcessorPosting: draft.confirmedNoDuplicateProcessorPosting,
      },
      draft
    );
    setDraft(
      draft.version === 3 && mappingChanged
        ? { ...next, confirmedNoDuplicateProcessorPosting: false }
        : next
    );
    setError('');
    setNotice('');
  };
  const editRep = (actorId, key, value) => {
    const reps = draft.repCustomers;
    const existing = reps.find((rep) => rep.actorId === actorId);
    const updated = {
      actorId,
      qbCustomerId: '',
      expectedDisplayName: '',
      ...existing,
      [key]: value,
    };
    edit({
      ...draft,
      repCustomers: existing
        ? reps.map((rep) => (rep.actorId === actorId ? updated : rep))
        : [...reps, updated],
    });
  };
  const save = async (event) => {
    event.preventDefault();
    if (
      pending.current ||
      !current() ||
      disabled ||
      uncertain ||
      !dirty ||
      ![2, 3].includes(saved?.schemaVersion) ||
      distributor.isActive !== true
    )
      return;
    const configuration =
      draft.version === 3
        ? {
            ...draft,
            repCustomers: draft.repCustomers.filter(
              (rep) => rep.qbCustomerId || rep.expectedDisplayName
            ),
          }
        : draft;
    if (!validFundingConfiguration(configuration))
      return setError(
        draft.version === 3
          ? 'Enter verified QuickBooks account IDs, each purchasing rep customer ID and exact display name, and an approval reference.'
          : 'Enter verified QuickBooks account IDs, the distributor buyer ID and legal name, an approval reference, and the duplicate-posting confirmation.'
      );
    const request = begin(),
      expectedRevision = saved.revision;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const row = readFundingSettings(
        unwrap(
          await distributorService.saveAdminCashFundingSettings(distributor._id, {
            expectedRevision,
            configuration,
          })
        )
      );
      if (!request.isCurrent()) return;
      if (
        row.revision !== expectedRevision + 1 ||
        !sameFundingConfiguration(configurationFor(row), configuration)
      )
        throw new Error('Unconfirmed funding configuration');
      setSaved(row);
      setDraft(configurationFor(row));
      setNotice(
        row.schemaVersion === 3
          ? 'Bookkeeping draft saved. Payment verification is pending. Saving does not enable payments or post QuickBooks entries.'
          : 'Cash payment setup saved as a draft. Saving does not enable payments or post QuickBooks entries.'
      );
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError(
          'Cash payment setup could not be confirmed. Discard and reload the saved configuration before editing again.'
        );
      }
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  if (!available) return null;
  return (
    <fieldset className="me-cash me-cash-card" aria-label={t('Cash payment setup')}>
      <legend>{t('Cash payment setup')}</legend>
      <div className="me-cash-heading">
        <h2>
          {t(
            saved?.schemaVersion === 3
              ? 'QuickBooks accounts & purchasing reps'
              : 'QuickBooks accounts & distributor buyer'
          )}
        </h2>
        <span>{t('Draft only')}</span>
      </div>
      <p>
        {t(
          'Cash deposits are recorded as prepaid customer funds, separate from product revenue. Use only verified QuickBooks record IDs; do not enter bank account numbers.'
        )}
      </p>
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {!saved && !error && <p role="status">{t('Loading cash payment setup…')}</p>}
      {(!saved || uncertain) && error && (
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => {
            setRetry((value) => value + 1);
            setExpanded(true);
          }}
        >
          {t('Discard and reload payment setup')}
        </button>
      )}
      {saved && (
        <>
          <p className="me-cash-note">
            {t(
              saved.readiness.code === 'not_configured'
                ? 'Cash payment accounting is not configured.'
                : saved.readiness.code === 'disabled'
                  ? 'Cash payment accounting is disabled. Live funding remains unavailable.'
                  : saved.readiness.code === 'processor_posting_verification_required'
                    ? 'Draft — payment verification pending.'
                    : 'Cash payment accounting requires verification before funding is available.'
            )}
          </p>
          {(saved.legacyReadOnly ||
            saved.schemaVersion === 1 ||
            (saved.schemaVersion === 3 && saved.policy && saved.policy.version !== 3)) && (
            <p role="status">
              {t(
                saved.schemaVersion === 3
                  ? 'The previous accounting configuration is preserved. Enter an explicit purchasing rep draft; existing financial history may require reconciliation before replacement.'
                  : 'Legacy rep accounting is read-only. Enter a complete organization configuration to replace it; existing financial history may require reconciliation first.'
              )}
            </p>
          )}
          {!expanded && [2, 3].includes(saved.schemaVersion) && (
            <button
              type="button"
              disabled={disabled || distributor.isActive !== true}
              onClick={() => setExpanded(true)}
            >
              {t('Configure cash payments')}
            </button>
          )}
          {expanded && draft && [2, 3].includes(saved.schemaVersion) && (
            <form onSubmit={save}>
              <fieldset disabled={disabled || busy || uncertain || distributor.isActive !== true}>
                <div className="me-cash-grid">
                  <label>
                    {t('Prepaid liability account ID')}
                    <input
                      value={draft.liabilityAccountId}
                      maxLength={200}
                      onChange={(event) =>
                        edit({ ...draft, liabilityAccountId: event.target.value })
                      }
                    />
                  </label>
                  {['wire', 'ach', 'card'].map((method) => (
                    <label key={method}>
                      {t(
                        {
                          wire: 'Wire deposit account ID (optional)',
                          ach: 'ACH clearing account ID (optional)',
                          card: 'Card clearing account ID (optional)',
                        }[method]
                      )}
                      <input
                        value={draft.clearingAccounts[method]}
                        maxLength={200}
                        onChange={(event) =>
                          edit({
                            ...draft,
                            clearingAccounts: {
                              ...draft.clearingAccounts,
                              [method]: event.target.value,
                            },
                          })
                        }
                      />
                    </label>
                  ))}
                  {[
                    ...(draft.version === 3
                      ? []
                      : [
                          ['qbCustomerId', 'Distributor QuickBooks customer ID'],
                          ['legalBuyerName', 'Distributor legal buyer name'],
                        ]),
                    ['productIncomeAccountId', 'Product income account ID'],
                    ['shippingIncomeAccountId', 'Shipping income account ID'],
                  ].map(([key, label]) => (
                    <label key={key}>
                      {t(label)}
                      <input
                        value={draft[key]}
                        maxLength={200}
                        onChange={(event) => edit({ ...draft, [key]: event.target.value })}
                      />
                    </label>
                  ))}
                </div>
                {draft.version === 3 && (
                  <fieldset>
                    <legend>{t('Purchasing rep customer mappings')}</legend>
                    <p>
                      {t(
                        'Each purchasing rep uses their own verified QuickBooks customer. All reps share the same Virtual Tissue Bank Balance.'
                      )}
                    </p>
                    <p>
                      {t(
                        'These customer mappings identify the funding rep only. Spending from the shared balance is not billed to a rep.'
                      )}
                    </p>
                    {saved.repCandidates.length === 0 && (
                      <p>
                        {t(
                          'No eligible purchasing reps are available. Add an active purchasing rep before completing this draft.'
                        )}
                      </p>
                    )}
                    {saved.repCandidates.map((rep) => {
                      const mapping = draft.repCustomers.find((row) => row.actorId === rep.actorId);
                      return (
                        <fieldset key={rep.actorId}>
                          <legend>{rep.displayName}</legend>
                          <div className="me-cash-grid">
                            <label>
                              {t('QuickBooks customer ID')}
                              <input
                                aria-label={t('QuickBooks customer ID for {rep}', {
                                  rep: rep.displayName,
                                })}
                                value={mapping?.qbCustomerId || ''}
                                maxLength={200}
                                onChange={(event) =>
                                  editRep(rep.actorId, 'qbCustomerId', event.target.value)
                                }
                              />
                            </label>
                            <label>
                              {t('Exact QuickBooks display name')}
                              <input
                                aria-label={t('QuickBooks display name for {rep}', {
                                  rep: rep.displayName,
                                })}
                                value={mapping?.expectedDisplayName || ''}
                                maxLength={200}
                                onChange={(event) =>
                                  editRep(rep.actorId, 'expectedDisplayName', event.target.value)
                                }
                              />
                            </label>
                          </div>
                        </fieldset>
                      );
                    })}
                    {draft.repCustomers
                      .filter(
                        (rep) =>
                          !saved.repCandidates.some(
                            (candidate) => candidate.actorId === rep.actorId
                          )
                      )
                      .map((rep) => (
                        <p key={rep.actorId}>
                          {t(
                            'A saved rep mapping is retained for review and cannot be edited here.'
                          )}
                          : {rep.expectedDisplayName}
                        </p>
                      ))}
                  </fieldset>
                )}
                <p>
                  {t(
                    'All reps fund the same organization balance. Deposits remain prepaid funds; product and shipping revenue is recorded when orders spend that balance. Blank payment-method accounts keep those methods unavailable.'
                  )}
                </p>
                <label>
                  {t('Accounting approval reference')}
                  <input
                    value={draft.approvalReference}
                    maxLength={120}
                    onChange={(event) => edit({ ...draft, approvalReference: event.target.value })}
                  />
                </label>
                <label className="me-cash-authorize">
                  <input
                    type="checkbox"
                    checked={draft.confirmedNoDuplicateProcessorPosting}
                    onChange={(event) =>
                      edit({ ...draft, confirmedNoDuplicateProcessorPosting: event.target.checked })
                    }
                  />
                  {t(
                    'I verified that these records will not duplicate automatic QuickBooks payment posting.'
                  )}
                </label>
                {draft.version === 3 && (
                  <p className="me-cash-note">
                    {t(
                      'Leave this unchecked until automatic processor posting has been independently verified. You can save the draft without it.'
                    )}
                  </p>
                )}
                <div className="me-cash-actions">
                  <button
                    type="button"
                    disabled={!dirty}
                    onClick={() => {
                      setDraft(configurationFor(saved));
                      setError('');
                    }}
                  >
                    {t('Discard edits')}
                  </button>
                  <button className="me-cash-primary" type="submit" disabled={!dirty}>
                    {t('Save draft payment setup')}
                  </button>
                </div>
              </fieldset>
            </form>
          )}
        </>
      )}
    </fieldset>
  );
}
